import { useEffect, useRef, useState } from 'react';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { ArtistPortalArtistRef } from 'fym-shared/src/smartlinks/artistPortals';
import { useCompleteArtistPortalAuthMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import {
  forgetSoundCloudConnect,
  markSoundCloudConnectFailed,
  soundCloudConnectAccount,
} from '../../../music24/soundcloud/soundCloudConnectSession';
import { soundCloudReturnError, SoundCloudReturnError } from './soundCloudReturnOutcome';

export type SoundCloudReturnState =
  | { kind: 'working' }
  | { kind: 'connected'; artist: ArtistPortalArtistRef | null; returnTo: string | null }
  | { kind: 'error'; error: SoundCloudReturnError };

/** How long the connected page shows before it sends the artist back to `returnTo` (design b2 note). */
export const RETURN_TO_DELAY_MS = 1000;

/**
 * The work of the return page (design b1): `auth/complete` one time with the `state` of the
 * address. The account is the one that started the sign-in in this tab, else the active artist.
 */
export function useSoundCloudReturn(state: string): SoundCloudReturnState {
  const activeAccountId = useAccountId();
  const accountId = soundCloudConnectAccount(state) ?? activeAccountId;
  const [complete] = useCompleteArtistPortalAuthMutation();
  const [result, setResult] = useState<SoundCloudReturnState>({ kind: 'working' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current || accountId === null || accountId === undefined) {
      return;
    }
    started.current = true;
    complete({
      accountId,
      integrationType: MusicAPIIntegrationID.SoundCloud,
      body: { sessionUuid: state, redirectUrl: window.location.href },
    })
      .then((answer) => {
        forgetSoundCloudConnect();
        if (!isSuccessfulResult(answer)) {
          markSoundCloudConnectFailed(true);
          setResult({ kind: 'error', error: soundCloudReturnError(answer.error) });
          return;
        }
        markSoundCloudConnectFailed(false);
        setResult({
          kind: 'connected',
          artist: answer.data.connection.artist ?? answer.data.artists[0] ?? null,
          returnTo: answer.data.returnTo ?? null,
        });
      })
      .catch(console.error);
  }, [accountId, complete, state]);

  useEffect(() => {
    if (result.kind !== 'connected' || result.returnTo === null) {
      return undefined;
    }
    const target = result.returnTo;
    const timer = window.setTimeout(() => window.location.assign(target), RETURN_TO_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [result]);

  return result;
}
